import '../styles/reader.css'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import type { Block, ImageFrame, MarginNoteBlock, Narration, UnfoldBlock } from '../lib/types'
import { collectSources, isOutOfFlow } from '../lib/blocks'
import { isNarrated } from '../lib/narration'
import { longDate, readMinutes } from '../lib/text'
import { safeBlocks } from '../lib/urls'
import { Avatar } from './ui'
import { FramedImage } from './FramedImage'
import { ArticleBody } from './reader/ArticleBody'
import { ListenPlayer, type ListenController, type ListenState } from './reader/ListenPlayer'
import { SelectionToolbar } from './reader/SelectionToolbar'
import { marginNoteId, ReaderContext, unfoldCardId, type ReaderContextValue, type TakeActions } from './reader/context'
import { prefersReducedMotion, useAsideLayout, useFloatNeighbours, useFollowNarration, useReadingFocal } from './reader/hooks'
import type { NoteControls, UnfoldControls } from './reader/InlineText'

export type { TakeActions } from './reader/context'

// The article as readers see it (design 4b), shared by the public reader and
// the author's preview: head, Listen, the column with its margins, sources.
// It owns what's local to reading — the Reading light's focal block, the
// narration's place, open unfolds. What a reader can DO with the words
// (take, copy, share) comes in as `takes`, from the public page only.

interface Props {
  title: string
  subtitle: string
  coverUrl: string | null
  /** The cover's crop & position. */
  coverFrame?: ImageFrame | null
  blocks: Block[]
  showTimes: boolean
  showSources: boolean
  author: { name: string; avatarUrl: string | null }
  date: string
  wordCount: number
  narration?: Narration | null
  /** Dim everything but the block being read. */
  readingLight?: boolean
  takes?: TakeActions
  children?: ReactNode
}

const NOT_LISTENING: ListenState = { activeId: null, playing: false, started: false, follow: false }

export function ArticleView({
  title,
  subtitle,
  coverUrl,
  coverFrame = null,
  blocks: rawBlocks,
  showTimes,
  showSources,
  author,
  date,
  wordCount,
  narration = null,
  readingLight = false,
  takes,
  children,
}: Props) {
  // Callers already filter (safePublished / safeWriting); filtering again here
  // means no reader surface can render an author's javascript: link by accident.
  const blocks = useMemo(() => safeBlocks(rawBlocks), [rawBlocks])
  const articleRef = useRef<HTMLElement>(null)
  const sources = showSources ? collectSources(blocks) : []

  // Narration: the player reports only when the block being read changes.
  const [listen, setListen] = useState<ListenState>(NOT_LISTENING)
  const onListen = useCallback(
    (s: ListenState) =>
      setListen((prev) =>
        prev.activeId === s.activeId && prev.playing === s.playing && prev.started === s.started && prev.follow === s.follow ? prev : s,
      ),
    [],
  )
  const controllerRef = useRef<ListenController | null>(null)
  const seekTo = useCallback((blockId: string) => controllerRef.current?.playFrom(blockId), [])
  const narrated = useMemo(() => new Set(narration ? blocks.filter(isNarrated).map((b) => b.id) : []), [blocks, narration])

  // The focal block: what the narration reads while it plays; else (Reading
  // light on) the column block whose words are selected, or the one the
  // reader's pointer is on, or (pointer off the text) the one at the reading
  // line as they scroll. The reader leads; nothing scrolls on its own.
  const readingFocal = useReadingFocal(articleRef, readingLight && !listen.playing, blocks)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const columnIds = useMemo(() => new Set(blocks.filter((b) => !isOutOfFlow(b)).map((b) => b.id)), [blocks])
  const selectedFocal = selectedId && columnIds.has(selectedId) ? selectedId : null
  const narrationFocal = listen.playing ? listen.activeId : null
  const focalId = narrationFocal ?? (readingLight ? (selectedFocal ?? readingFocal) : null)
  // The page follows the narration only when the reader turned on Follow.
  useFollowNarration(articleRef, listen.activeId, listen.playing && listen.follow)

  // Unfolds: the words they hang from toggle them open under their paragraph.
  const unfolds = useMemo(
    () => new Map(blocks.filter((b): b is UnfoldBlock => b.type === 'unfold').map((u) => [u.anchor, u])),
    [blocks],
  )
  const [openUnfolds, setOpenUnfolds] = useState<ReadonlySet<string>>(() => new Set())
  const toggleUnfold = useCallback(
    (anchor: string) =>
      setOpenUnfolds((prev) => {
        const next = new Set(prev)
        if (next.has(anchor)) next.delete(anchor)
        else next.add(anchor)
        return next
      }),
    [],
  )
  const closeUnfold = useCallback(
    (anchor: string) =>
      setOpenUnfolds((prev) => {
        if (!prev.has(anchor)) return prev
        const next = new Set(prev)
        next.delete(anchor)
        return next
      }),
    [],
  )
  const unfoldControls = useMemo<UnfoldControls>(
    () => ({ has: (a) => unfolds.has(a), isOpen: (a) => openUnfolds.has(a), toggle: toggleUnfold, cardId: unfoldCardId }),
    [unfolds, openUnfolds, toggleUnfold],
  )

  // Margin notes made from selected words: the words (underlined, as in the
  // editor) bring their note into view and light it for a moment.
  const noteByAnchor = useMemo(
    () => new Map(blocks.filter((b): b is MarginNoteBlock => b.type === 'margin' && !!b.anchor).map((m) => [m.anchor!, m.id])),
    [blocks],
  )
  const [litNote, setLitNote] = useState<string | null>(null)
  const litTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  useEffect(() => () => clearTimeout(litTimer.current), [])
  const notes = useMemo<NoteControls>(
    () => ({
      has: (a) => noteByAnchor.has(a),
      noteId: (a) => marginNoteId(noteByAnchor.get(a) ?? ''),
      show(a) {
        const id = noteByAnchor.get(a)
        if (!id) return
        document.getElementById(marginNoteId(id))?.scrollIntoView?.({ block: 'nearest', behavior: prefersReducedMotion() ? 'auto' : 'smooth' })
        setLitNote(id)
        clearTimeout(litTimer.current)
        litTimer.current = setTimeout(() => setLitNote(null), 1800)
      },
    }),
    [noteByAnchor],
  )

  // Sources fold to a count ("12 sources") until the reader opens them.
  const [sourcesOpen, setSourcesOpen] = useState(false)
  useEffect(() => {
    const open = () => window.location.hash === '#sources' && setSourcesOpen(true)
    open()
    window.addEventListener('hashchange', open)
    return () => window.removeEventListener('hashchange', open)
  }, [])

  const layoutKey = useMemo(() => ({}), [blocks, openUnfolds, sourcesOpen]) // eslint-disable-line react-hooks/exhaustive-deps
  useAsideLayout(articleRef, layoutKey)
  useFloatNeighbours(articleRef, layoutKey)
  // A lit paragraph that wraps a photo and runs on below it has a full-column
  // box under the photo, so that photo stays at full strength on top of it.
  const [alsoLitId, setAlsoLitId] = useState<string | null>(null)
  useLayoutEffect(() => {
    const el = focalId ? articleRef.current?.querySelector<HTMLElement>(`[data-focus="${focalId}"]`) : null
    setAlsoLitId(el?.dataset.floatOver ?? null)
  }, [focalId, layoutKey])

  const [activeQuote, setActiveQuote] = useState<string | null>(null)
  const firstVoiceId = useMemo(() => blocks.find((b) => b.type === 'voice')?.id ?? null, [blocks])

  const ctx = useMemo<ReaderContextValue>(
    () => ({
      takes,
      showTimes,
      authorName: author.name,
      unfolds,
      openUnfolds,
      unfoldControls,
      closeUnfold,
      notes,
      litNote,
      firstVoiceId,
      activeQuote,
      setActiveQuote,
      listening: listen.started,
      narrated,
      seekTo,
    }),
    [takes, showTimes, author.name, unfolds, openUnfolds, unfoldControls, closeUnfold, notes, litNote, firstVoiceId, activeQuote, listen.started, narrated, seekTo],
  )

  return (
    <ReaderContext.Provider value={ctx}>
      <article ref={articleRef} data-reading-light={readingLight ? 'on' : 'off'} className="wr-column font-serif text-foreground">
        {coverUrl && <FramedImage src={coverUrl} frame={coverFrame} className="mb-8 h-[220px] w-full rounded-2xl sm:mb-9 sm:h-[320px]" />}
        <h1 className="wr-t-title m-0 mb-3">{title || 'Untitled'}</h1>
        {subtitle && <p className="wr-t-subtitle m-0 mb-[22px]">{subtitle}</p>}
        <div
          className={`${narration ? 'mb-[18px]' : 'mb-[38px]'} flex flex-wrap items-center justify-between gap-3 border-b border-border/60 pb-[22px] font-display text-[14px] text-muted-foreground`}
        >
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <Avatar src={author.avatarUrl} name={author.name} size={26} className="mr-0.5" />
            <span className="text-foreground">{author.name}</span>
            <span aria-hidden>·</span>
            <span>{longDate(date)}</span>
            <span aria-hidden>·</span>
            <span>{readMinutes(wordCount)} min read</span>
          </div>
          {sources.length > 0 && (
            <a href="#sources" onClick={() => setSourcesOpen(true)} className="text-muted-foreground no-underline hover:underline">
              {sources.length} source{sources.length === 1 ? '' : 's'}
            </a>
          )}
        </div>
        {narration && (
          <ListenPlayer
            narration={narration}
            blocks={blocks}
            authorName={author.name}
            onState={onListen}
            controllerRef={controllerRef}
            className="mb-[38px]"
          />
        )}

        <ArticleBody blocks={blocks} focalId={focalId} dim={readingLight && focalId !== null} alsoLitId={alsoLitId} />

        <div aria-hidden className="clear-both my-12 flex justify-center gap-2.5">
          {[0, 1, 2].map((i) => (
            <span key={i} className="size-1 rounded-full bg-accent" />
          ))}
        </div>

        {sources.length > 0 && (
          <section id="sources" aria-label="Sources in this writing" className="scroll-mt-20 border-t border-border/60 pb-2 pt-5">
            <button
              type="button"
              aria-expanded={sourcesOpen}
              aria-controls="sources-list"
              onClick={() => setSourcesOpen((o) => !o)}
              className="-mx-1 flex items-center gap-1.5 rounded-md px-1 py-0.5 font-sans text-[13px] font-medium text-muted-foreground hover:text-foreground"
            >
              {sources.length} source{sources.length === 1 ? '' : 's'}
              <svg aria-hidden viewBox="0 0 12 12" className={`size-3 transition-transform ${sourcesOpen ? 'rotate-180' : ''}`}>
                <path d="M3 4.5l3 3 3-3" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            {sourcesOpen && (
              <ol id="sources-list" className="m-0 mt-3.5 flex list-none flex-col gap-2.5 p-0 text-[15px]/[1.4]">
                {sources.map((s, i) => (
                  <li key={`${s.url ?? s.title}-${i}`} className="flex gap-3">
                    <span className="w-[18px] shrink-0 pt-[3px] font-sans text-[13px] font-medium text-muted-foreground">{i + 1}</span>
                    <span>
                      {s.url ? (
                        <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-foreground no-underline hover:underline">
                          {s.title}
                        </a>
                      ) : (
                        s.title
                      )}
                      {s.site && s.site !== s.title && <span className="text-muted-foreground"> · {s.site}</span>}
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </section>
        )}

        {children}
      </article>
      {takes && <SelectionToolbar rootRef={articleRef} onTake={takes.takePassage} onCopy={takes.copyLine} onBlock={setSelectedId} />}
    </ReaderContext.Provider>
  )
}
